import { Component, type ReactNode } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router';
import { useTheme } from '@/contexts/ThemeContext';

// ── Route Error Element (for errorElement prop) ──────────────────────

export function RouteErrorScreen() {
  const error = useRouteError();

  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Please try again.';

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = 'Page not found';
      message = 'The page you were looking for could not be found.';
    } else {
      title = `Error ${error.status}`;
      message = error.statusText || message;
    }
  } else if (error instanceof Error) {
    message = error.message;
    // Report to JS error endpoint
    const api = (window as any).api;
    const JSErrorType = (window as any).JSErrorType;
    if (api?.insertNewJSError && JSErrorType) {
      const fullError = `${error.message}\n${error.stack || ''}`;
      api.insertNewJSError(fullError, window.location.href, error.message, JSErrorType.UNCAUGHT, null);
    }
  }

  return <ErrorDisplay title={title} message={message} />;
}

// ── Class-based Error Boundary (for component tree errors) ──────────

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: any) {
    console.error('ErrorBoundary caught:', error, info?.componentStack);
    // Report to JS error endpoint
    const api = (window as any).api;
    const JSErrorType = (window as any).JSErrorType;
    if (api?.insertNewJSError && JSErrorType) {
      const fullError = `${error.message}\n${error.stack || ''}\nComponent Stack: ${info?.componentStack || ''}`;
      api.insertNewJSError(fullError, window.location.href, error.message, JSErrorType.UNCAUGHT, null);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          title="Something went wrong"
          message={this.state.error?.message || 'An unexpected error occurred.'}
          onRetry={() => this.setState({ hasError: false, error: null })}
        />
      );
    }
    return this.props.children;
  }
}

// ── Shared Error Display ────────────────────────────────────────────

function ErrorDisplay({ title, message, onRetry }: { title: string; message: string; onRetry?: () => void }) {
  // Safe even when this renders after a crash: ThemeContext is created with a
  // default value, so useContext never throws when no provider is present.
  const { theme } = useTheme();
  const isDark = theme === 'theme-gray-dark' || theme === 'theme-blue-dark';

  const titleColor = isDark ? '#e5e5e5' : '#1f2937';
  const messageColor = isDark ? '#b0b0b0' : '#6b7280';
  const secondaryButtonBackground = isDark ? 'transparent' : '#fff';
  const secondaryButtonColor = isDark ? '#e5e5e5' : '#374151';
  const secondaryButtonBorder = isDark ? '1px solid #555' : '1px solid #d1d5db';

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '300px',
      padding: '2rem',
      textAlign: 'center',
      fontFamily: "'Poppins', sans-serif",
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        background: '#fee2e2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '1rem',
        fontSize: '24px',
      }}>
        !
      </div>
      <h2 style={{
        margin: '0 0 0.5rem',
        fontSize: '1.25rem',
        fontWeight: 600,
        color: titleColor,
      }}>{title}</h2>
      <p style={{
        margin: '0 0 1.5rem',
        fontSize: '0.875rem',
        color: messageColor,
        maxWidth: '400px',
      }}>{message}</p>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={() => window.location.href = '/home'}
          style={{
            padding: '0.5rem 1rem',
            border: secondaryButtonBorder,
            borderRadius: '6px',
            background: secondaryButtonBackground,
            color: secondaryButtonColor,
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          Go Home
        </button>
        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '6px',
              background: '#2563eb',
              color: '#fff',
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  );
}
